import React, { useMemo, useState } from 'react'

import { joiResolver } from '@hookform/resolvers/joi'
import CloseIcon from '@mui/icons-material/Close'
import { Button, Grid, IconButton, Tooltip } from '@mui/material'
import { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid'
import Joi from 'joi'
import { useForm } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import { FplDataGrid, ControlledTextField, SplitButton } from 'components/common'
import {
  DEBOUNCE_DELAY,
  SupplierAllInvoicesButtonOptions,
  SupplierInvoiceFileTypeValues,
  SupplierInvoiceTypeButtonsId,
  SupplierSelectedInvoicesButtonOptions,
} from 'constants/index'
import { BookingInvoiceFilterInput } from 'generated/graphql'
import { GET_PAGED_DRIVER_INVOICES } from 'graphql/queries'
import { DisableSubmit, GetFileName, SaveFile, ShowRestError } from 'helpers'
import { useGetDriverInvoicesColumns } from 'hooks/fplDataGridColumns/useGetDriverInvoicesColumns'
import { Restricted } from 'providers/PermissionProvider'
import { DownloadSelectedDriverInvoicesReport, DownloadAllDriverInvoicesReport } from 'services'

import { GenerateInvoiceDialog } from './GenerateInvoiceDialog'

interface IProps {
  driverId: number
}

const searchSchema = Joi.object({
  search: Joi.number()
    .integer()
    .allow(null, '')
    .empty('')
    .optional()
    .min(1)
    .max(Number.MAX_SAFE_INTEGER),
})

const DriverInvoices = (props: IProps) => {
  const { driverId } = props

  const columns = useGetDriverInvoicesColumns()

  const {
    control,
    watch,
    resetField,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(searchSchema),
    mode: 'onChange',
    defaultValues: {
      search: '',
    },
  })
  const [isAllProcessing, setIsAllProcessing] = useState(false)

  const [isSelectedProcessing, setIsSelectedProcessing] = useState(false)

  const [isGenerateInvoiceOpen, setIsGenerateInvoiceOpen] = useState(false)

  const [selectedInvocesIds, setselectedInvocesIds] = useState<GridRowId[]>([])
  const disabledOption = selectedInvocesIds?.length === 0

  const searchValue = watch('search', '')

  const [debouncedSearchValue] = useDebounce(errors?.search ? null : searchValue, DEBOUNCE_DELAY)

  const handleSearchClearClick = () => {
    resetField('search', { keepDirty: false, keepError: false, keepTouched: false })
  }

  const where: BookingInvoiceFilterInput | undefined = React.useMemo(() => {
    if (debouncedSearchValue) {
      return { invoiceNumber: { eq: Number(debouncedSearchValue) } }
    } else {
      return undefined
    }
  }, [debouncedSearchValue])

  const queryData = useMemo(
    () => ({
      query: GET_PAGED_DRIVER_INVOICES,
      entityName: 'pagedDriverInvoices',
      queryVariables: {
        driverId,
      },
    }),
    [driverId],
  )

  const handleSelectionModelChange = (newSelectionModel: GridRowSelectionModel) => {
    setselectedInvocesIds(newSelectionModel)
  }

  const onDownloadSelectedButtonClick = (buttonId: SupplierInvoiceTypeButtonsId): void => {
    const invoiceFileType = SupplierInvoiceFileTypeValues[buttonId]

    if (!invoiceFileType?.id) {
      return
    }

    setIsSelectedProcessing(true)

    DownloadSelectedDriverInvoicesReport(invoiceFileType.id, selectedInvocesIds)
      .then((response) => {
        setIsSelectedProcessing(false)
        const fileName = GetFileName(response) ?? `driver-invoice-report.${invoiceFileType.format}`

        SaveFile(response.data, fileName)
      })
      .catch(ShowRestError)
      .finally(() => {
        setIsSelectedProcessing(false)
      })
  }

  const onDownloadAllButtonClick = (buttonId: SupplierInvoiceTypeButtonsId): void => {
    const invoiceFileType = SupplierInvoiceFileTypeValues[buttonId]

    if (!invoiceFileType?.id) {
      return
    }

    setIsAllProcessing(true)

    DownloadAllDriverInvoicesReport(invoiceFileType.id, driverId)
      .then((response) => {
        setIsAllProcessing(false)

        const fileName = GetFileName(response) ?? `driver-invoice-report.${invoiceFileType.format}`

        SaveFile(response.data, fileName)
      })
      .catch(ShowRestError)
      .finally(() => {
        setIsAllProcessing(false)
      })
  }

  return (
    <>
      <FplDataGrid
        query={queryData.query}
        entityName={queryData.entityName}
        queryVariables={queryData.queryVariables}
        skipQuery={!queryData.queryVariables.driverId}
        columns={columns}
        checkboxSelection
        onSelectionModelChange={handleSelectionModelChange}
        defaultOrder={{ field: 'invoiceNumber', sort: 'desc' }}
        filter={where}
        pageOptions={{
          pageSize: 10,
          rowsPerPage: [10, 50, 100],
        }}
        toolbar={{
          caption: 'Invoices',
          rightSide: (
            <>
              <Restricted to='GenerateManualDriverInvoice'>
                <Grid item>
                  <Tooltip title='Manually generate supplier invoices and APRs for delivered bookings missed by automatic generation, for example when the driver never accepted the job.'>
                    <Button variant='contained' onClick={() => setIsGenerateInvoiceOpen(true)}>
                      Generate Invoice
                    </Button>
                  </Tooltip>
                </Grid>
              </Restricted>

              <Grid item>
                <form autoComplete='off' onSubmit={DisableSubmit}>
                  <ControlledTextField
                    control={control}
                    name='search'
                    label='Search by Invoice Number'
                    defaultValue=''
                    size='small'
                    error={!!errors.search}
                    helperText={errors.search?.message}
                    endAdornment={
                      searchValue && (
                        <IconButton size='small' onClick={handleSearchClearClick}>
                          <CloseIcon fontSize='small' />
                        </IconButton>
                      )
                    }
                  />
                </form>
              </Grid>

              <Grid item>
                <SplitButton
                  disabled={!disabledOption}
                  loading={isAllProcessing}
                  options={SupplierAllInvoicesButtonOptions}
                  onButtonClick={onDownloadAllButtonClick}
                />
              </Grid>

              <Grid item>
                <SplitButton
                  disabled={disabledOption}
                  options={SupplierSelectedInvoicesButtonOptions}
                  loading={isSelectedProcessing}
                  onButtonClick={onDownloadSelectedButtonClick}
                />
              </Grid>
            </>
          ),
        }}
      />

      <GenerateInvoiceDialog
        driverId={driverId}
        open={isGenerateInvoiceOpen}
        onClose={() => setIsGenerateInvoiceOpen(false)}
      />
    </>
  )
}

export default DriverInvoices
